import { useEffect, useMemo, useRef, useState } from "react";
import { Landmark, Info, Sparkles, ArrowRight, Copy, Share2, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ComplianceStrip from "../components/ComplianceStrip";
import ConveyancingCTA from "../components/ConveyancingCTA";
import SurveyQuoteCTA from "../components/SurveyQuoteCTA";
import BrokerCTA from "../components/BrokerCTA";
import { CashbackPromptCard } from "../components/CashbackPrompt";
import { cashbackPromotionEnabled } from "../config/promotions";
import Seo from "../components/Seo";
import SeoProse from "../components/SeoProse";
import { PAGES } from "../content/site";
import { fmtGBP } from "../lib/calc";
import { track } from "../lib/api";
import { JURISDICTIONS, TAX_CONFIG, RATES_LAST_REVIEWED, calculatePropertyTax, priceBand } from "../lib/propertyTax";

const meta = PAGES["/stamp-duty-calculator"];

// Show pence only when the amount is not a whole pound (e.g. £15,000.05 after
// FTB relief is lost above the threshold).
const fmtTax = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const hasPence = Math.round(n * 100) % 100 !== 0;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: hasPence ? 2 : 0,
    maximumFractionDigits: hasPence ? 2 : 0,
  }).format(n);
};

const BUYER_TYPES = [
  { id: "first_time_buyer", label: "First-time buyer" },
  { id: "home_mover", label: "Moving home / replacing my main residence" },
  { id: "additional_property", label: "Additional property / buy-to-let" },
];

const BUYER_LABELS = {
  first_time_buyer: "First-time buyer",
  home_mover: "Moving home / replacing main residence",
  additional_property: "Additional property / buy-to-let",
};

const BUYER_HELP = {
  first_time_buyer:
    "Everyone buying must never have owned a dwelling before (anywhere in the world) AND must intend to occupy this property as their only or main home.",
  home_mover:
    "You are replacing your main residence with this purchase. Whether higher rates apply also depends on whether your previous main home has been sold by the completion date — we'll ask below.",
  additional_property:
    "You already own another property and will still own it after this purchase (e.g. a second home, holiday home or buy-to-let).",
};

const PillButton = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`rounded-xl border px-4 py-3 text-sm font-medium transition-colors text-left ${
      active
        ? "border-[#0F766E] bg-[#0F766E]/8 text-[#0B5F58] font-semibold"
        : "border-[#DCE7E3] bg-white text-[#475569] hover:border-[#0F766E]/40 hover:text-[#0F766E]"
    }`}
  >
    {children}
  </button>
);

const InfoTip = ({ label, tip, testid }) => (
  <span className="inline-flex items-center gap-1.5">
    <span>{label}</span>
    <span
      className="group relative inline-flex items-center"
      tabIndex={0}
      data-testid={testid}
      aria-label={tip}
    >
      <Info className="w-3.5 h-3.5 text-[#0F766E]/70 cursor-help" />
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-lg border border-[#DCE7E3] bg-white p-3 text-xs font-normal leading-relaxed text-[#475569] shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
      >
        {tip}
      </span>
    </span>
  </span>
);

const bandLabel = (row) => {
  if (row.to == null) return `Over ${fmtGBP(row.from)}`;
  const from = row.from === 0 ? "£0" : fmtGBP(row.from + 1);
  return `${from} – ${fmtGBP(row.to)}`;
};

const JURISDICTION_LABELS = {
  england: "England & Northern Ireland",
  "northern-ireland": "England & Northern Ireland",
  wales: "Wales",
  scotland: "Scotland",
};

/* Build a plain-text shareable summary of the CURRENT calculation.
   Uses the same `result` object rendered on-screen so there's zero risk of
   the shared text diverging from what the user sees. No customer-identifying
   data, no partner names, no query strings. */
const buildShareText = ({ price, buyerType, residency, previousHomeSold, jurisdiction, result }) => {
  const location = JURISDICTION_LABELS[jurisdiction] || jurisdiction;
  const buyerLabels = {
    first_time_buyer: "First-time buyer",
    home_mover: "Moving home / replacing main residence",
    additional_property: "Additional property / buy-to-let",
  };
  const notes = [];
  if (result.reliefApplied) notes.push("First-time buyer relief applied");
  if (result.higherRatesApplied && result.higherRatesReason === "additional") notes.push("Additional-property higher rates applied");
  if (result.higherRatesApplied && result.higherRatesReason === "not-replacing") notes.push("Higher rates applied (previous main home not sold by completion)");
  if (result.nonResidentApplied) notes.push("Non-UK resident surcharge (+2%) applied");
  if (residency === "unsure" && jurisdiction !== "wales" && jurisdiction !== "scotland") notes.push("SDLT residence status: unsure — please confirm with your conveyancer");
  if (buyerType === "home_mover" && previousHomeSold === "unsure" && (jurisdiction === "england" || jurisdiction === "northern-ireland")) notes.push("Previous home sold by completion: unsure — please confirm with your conveyancer");

  const money = (n) => {
    const hasPence = Math.round(n * 100) % 100 !== 0;
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: hasPence ? 2 : 0 }).format(n);
  };
  const rows = result.rows
    .filter((r) => r.tax > 0)
    .map((r) => `• ${money(r.tax)} at ${r.rate}% on ${bandLabel(r)}`);
  if (result.supplement) rows.push(`• ${money(result.supplement.amount)} — ${result.supplement.name} on the full price (${result.supplement.rate}%)`);
  if (rows.length === 0) rows.push("• No stamp duty payable on this purchase");

  return [
    `MortgagePath Stamp Duty estimate`,
    ``,
    `Purchase price: ${money(price)}`,
    `Location: ${location}`,
    `Buyer status: ${buyerLabels[buyerType] || buyerType}`,
    ...(notes.length ? [``, `Notes:`, ...notes.map((n) => `• ${n}`)] : []),
    ``,
    `Estimated ${result.shortName}: ${money(result.total)}`,
    ``,
    `Breakdown:`,
    ...rows,
    ``,
    `This is an educational estimate based on the information entered and the published tax rules in force on the date shown. Check the final amount with your solicitor/conveyancer or the relevant tax authority (HMRC / Revenue Scotland / Welsh Revenue Authority) before relying on it.`,
    ``,
    `Calculate your own figures at MortgagePath.uk/stamp-duty-calculator`,
  ].join("\n");
};

const AppliedChip = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0B5F58] bg-[#10B981]/10 border border-[#10B981]/30 rounded-full px-2.5 py-1">
    {children}
  </span>
);

/* Share actions — sit inside the result card, beneath the disclaimer.
   Uses navigator.share on capable devices (iOS/Android + some desktop
   browsers) and falls back to clipboard-write. No PII, no URL params. */
const ShareActions = ({ shareText }) => {
  const [copied, setCopied] = useState(false);
  const nativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success("Result copied");
      track("stamp_duty_copy_result", { has_native_share: nativeShare });
      setTimeout(() => setCopied(false), 2000);
      return true;
    } catch {
      toast.error("Couldn't copy — please long-press the breakdown above to copy manually.");
      return false;
    }
  };

  const doShare = async () => {
    if (!nativeShare) return doCopy();
    try {
      await navigator.share({ title: "MortgagePath Stamp Duty estimate", text: shareText });
      track("stamp_duty_share_result", { method: "native" });
    } catch (err) {
      // navigator.share throws AbortError when the user cancels — don't count as a share.
      if (err && err.name === "AbortError") return;
      // Any other failure -> fall back to clipboard, but record it as a share
      // (clipboard fallback) so GA4 attribution stays with the user's intent.
      const ok = await doCopy();
      if (ok) track("stamp_duty_share_result", { method: "clipboard_fallback" });
    }
  };

  return (
    <div
      className="mt-6 flex flex-wrap gap-2.5 items-center border-t border-[#DCE7E3] pt-5"
      data-testid="sd-share-actions"
      aria-label="Share this estimate"
    >
      <button
        type="button"
        onClick={doCopy}
        data-testid="sd-copy-result"
        className="inline-flex items-center gap-2 text-sm font-medium text-[#0F766E] bg-[#0F766E]/8 hover:bg-[#0F766E]/15 border border-[#0F766E]/25 hover:border-[#0F766E]/50 rounded-full px-4 py-2 transition-colors"
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        {copied ? "Copied" : "Copy breakdown"}
      </button>
      <button
        type="button"
        onClick={doShare}
        data-testid="sd-share-result"
        className="inline-flex items-center gap-2 text-sm font-medium text-[#0F766E] bg-[#0F766E]/8 hover:bg-[#0F766E]/15 border border-[#0F766E]/25 hover:border-[#0F766E]/50 rounded-full px-4 py-2 transition-colors"
      >
        <Share2 className="w-4 h-4" />
        Share result
      </button>
      <span className="text-xs text-[#94A3B8] ml-1">Shares text only — no personal data or partner details.</span>
    </div>
  );
};

const StampDuty = () => {
  const [jurisdiction, setJurisdiction] = useState("england");
  const [priceRaw, setPriceRaw] = useState("");
  const [buyerType, setBuyerType] = useState("first_time_buyer");
  const [residency, setResidency] = useState("yes");
  const [previousHomeSold, setPreviousHomeSold] = useState("yes");
  const startedRef = useRef(false);

  const markStarted = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      track("stamp_duty_calculator_started", {});
    }
  };

  const price = Number(priceRaw) || 0;
  const cfg = TAX_CONFIG[jurisdiction];
  const result = calculatePropertyTax({ jurisdiction, price, buyerType, residency, previousHomeSold });

  const shareText = useMemo(
    () => (result ? buildShareText({ price, buyerType, residency, previousHomeSold, jurisdiction, result }) : ""),
    [price, buyerType, residency, previousHomeSold, jurisdiction, result]
  );

  useEffect(() => {
    if (!price || price <= 0) return;
    const t = setTimeout(() => {
      track("stamp_duty_calculated", { jurisdiction, buyer_type: buyerType, purchase_price_band: priceBand(price) });
    }, 1200);
    return () => clearTimeout(t);
  }, [jurisdiction, price, buyerType, residency, previousHomeSold]);

  const onPriceChange = (e) => {
    markStarted();
    setPriceRaw(e.target.value.replace(/[^0-9]/g, "").slice(0, 9));
  };

  const showPrevHomeQ =
    cfg.hasPreviousHomeQuestion && buyerType === "home_mover";

  return (
    <div className="mp-container py-14">
      <Seo title={meta.title} description={meta.description} path="/stamp-duty-calculator" />
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-[#0F766E] flex items-center gap-2">
          <Landmark className="w-3.5 h-3.5" /> Free tool
        </div>
        <h1 className="font-sora mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-[#0B1220]">
          Stamp Duty &amp; Property Tax Calculator
        </h1>
        <p className="mt-4 text-[#475569] leading-relaxed text-base sm:text-lg max-w-2xl">
          Estimate the property tax you may pay when buying a home in England, Northern Ireland, Wales or Scotland.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 mt-10">
        {/* FORM */}
        <div className="lg:col-span-5">
          <div className="mp-card p-6 sm:p-7 lg:sticky lg:top-24">
            <div>
              <span className="mp-label">Where is the property?</span>
              <div className="grid grid-cols-2 gap-2.5 mt-1">
                {JURISDICTIONS.map((j) => (
                  <PillButton
                    key={j.id}
                    active={jurisdiction === j.id}
                    onClick={() => { markStarted(); setJurisdiction(j.id); }}
                    testid={`sd-jurisdiction-${j.id}`}
                  >
                    {j.label}
                  </PillButton>
                ))}
              </div>
            </div>

            <label className="block mt-6">
              <span className="mp-label">Property purchase price</span>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#475569] font-medium">£</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="mp-input !pl-8"
                  placeholder="e.g. 300,000"
                  value={priceRaw ? Number(priceRaw).toLocaleString("en-GB") : ""}
                  onChange={onPriceChange}
                  data-testid="sd-price-input"
                  aria-label="Property purchase price in pounds"
                />
              </div>
            </label>

            <div className="mt-6">
              <span className="mp-label">
                <InfoTip
                  label="What best describes the purchase?"
                  tip={BUYER_HELP[buyerType]}
                  testid={`sd-buyer-help-${buyerType}`}
                />
              </span>
              <div className="grid gap-2.5 mt-1">
                {BUYER_TYPES.map((b) => (
                  <PillButton
                    key={b.id}
                    active={buyerType === b.id}
                    onClick={() => { markStarted(); setBuyerType(b.id); }}
                    testid={`sd-buyer-${b.id}`}
                  >
                    {b.label}
                  </PillButton>
                ))}
              </div>
            </div>

            {showPrevHomeQ && (
              <div className="mt-6" data-testid="sd-previous-home-question">
                <span className="mp-label">
                  <InfoTip
                    label="Will your existing main residence have been sold by the time you complete this purchase?"
                    tip="If you'll still own your previous main home on the completion date, the higher rates for additional properties initially apply. A refund may later be available if you sell your former home within the required window and other conditions are met."
                    testid="sd-previous-home-help"
                  />
                </span>
                <div className="grid grid-cols-3 gap-2.5 mt-1">
                  {["yes", "no", "unsure"].map((r) => (
                    <PillButton
                      key={r}
                      active={previousHomeSold === r}
                      onClick={() => { markStarted(); setPreviousHomeSold(r); }}
                      testid={`sd-previous-home-${r}`}
                    >
                      <span className="block text-center capitalize w-full">{r}</span>
                    </PillButton>
                  ))}
                </div>
              </div>
            )}

            {cfg.hasResidencyQuestion && (
              <div className="mt-6" data-testid="sd-residency-question">
                <span className="mp-label">
                  <InfoTip
                    label="Are you a UK resident for SDLT purposes?"
                    tip="SDLT residence rules are specific to the transaction and may differ from your normal tax residence. If you're unsure, your conveyancer can confirm."
                    testid="sd-residency-help"
                  />
                </span>
                <div className="grid grid-cols-3 gap-2.5 mt-1">
                  {["yes", "no", "unsure"].map((r) => (
                    <PillButton
                      key={r}
                      active={residency === r}
                      onClick={() => { markStarted(); setResidency(r); }}
                      testid={`sd-residency-${r}`}
                    >
                      <span className="block text-center capitalize w-full">{r}</span>
                    </PillButton>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RESULT */}
        <div className="lg:col-span-7">
          {!result ? (
            <div className="mp-card p-8 sm:p-10 text-center" data-testid="sd-result-empty">
              <Landmark className="w-8 h-8 text-[#0F766E]/40 mx-auto" />
              <p className="mt-4 text-[#475569]">Enter a purchase price to see your estimated {cfg.taxName} ({cfg.shortName}).</p>
            </div>
          ) : (
            <div className="mp-card p-6 sm:p-8" data-testid="sd-result">
              <div className="text-xs uppercase tracking-widest text-[#0F766E]">Estimated {cfg.shortName} payable</div>
              <div className="font-sora text-4xl sm:text-5xl font-bold text-[#0B5F58] mt-2 tabular-nums" data-testid="sd-result-total">
                {fmtTax(result.total)}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {result.reliefApplied && <AppliedChip>First-time buyer relief applied</AppliedChip>}
                {result.higherRatesApplied && result.higherRatesReason === "additional" && <AppliedChip>Additional property rates applied</AppliedChip>}
                {result.higherRatesApplied && result.higherRatesReason === "not-replacing" && <AppliedChip>Higher rates applied — previous home not sold</AppliedChip>}
                {result.nonResidentApplied && <AppliedChip>Non-UK resident surcharge (+2%) applied</AppliedChip>}
              </div>

              <div className="mt-6 rounded-xl bg-[#F7FAF9] border border-[#DCE7E3] divide-y divide-[#DCE7E3] text-sm" data-testid="sd-result-meta">
                {[
                  ["Tax type", `${result.taxName} (${result.shortName})`],
                  ["Purchase price", fmtGBP(price)],
                  ["Buyer type", BUYER_LABELS[buyerType]],
                  ["Rates used", result.scheduleLabel],
                  ["Estimated tax", fmtTax(result.total)],
                  ["Effective tax rate", `${result.effectiveRate.toFixed(2)}%`],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-4 px-4 py-2.5">
                    <span className="text-[#475569]">{k}</span>
                    <span className="font-semibold text-[#0B1220] text-right">{v}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-[#0F766E]">How this was calculated</div>
                <div className="mt-2 rounded-xl border border-[#DCE7E3] overflow-hidden" data-testid="sd-breakdown">
                  {result.rows.map((row) => (
                    <div key={`${row.from}-${row.rate}`} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm border-b border-[#DCE7E3] last:border-b-0" data-testid="sd-band-row">
                      <span className="text-[#475569] tabular-nums">{bandLabel(row)}</span>
                      <span className="text-[#0F766E] font-medium tabular-nums shrink-0">{row.rate}%</span>
                      <span className="font-semibold text-[#0B1220] tabular-nums w-24 text-right shrink-0">{fmtTax(row.tax)}</span>
                    </div>
                  ))}
                  {result.supplement && (
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm bg-[#FFF7E6]" data-testid="sd-ads-row">
                      <span className="text-[#475569]">{result.supplement.name} on the full price</span>
                      <span className="text-[#B45309] font-medium tabular-nums shrink-0">{result.supplement.rate}%</span>
                      <span className="font-semibold text-[#0B1220] tabular-nums w-24 text-right shrink-0">{fmtTax(result.supplement.amount)}</span>
                    </div>
                  )}
                </div>
              </div>

              {result.higherRatesApplied && result.higherRatesReason === "not-replacing" && (
                <div className="mt-4 flex gap-2.5 rounded-xl bg-[#FFF7E6] border border-[#FCD98B] p-4 text-sm text-[#475569] leading-relaxed" data-testid="sd-refund-note">
                  <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#D97706]" />
                  <span>
                    Because your previous main residence will still be owned on the completion date, the higher rates
                    apply to this purchase. If you later sell your former main home within the relevant window and
                    the other conditions are met, you may be able to reclaim the additional amount from HMRC.
                    Simply having the old property on the market is not enough — your conveyancer can confirm the exact position.
                  </span>
                </div>
              )}

              {result.notes.map((n) => (
                <div key={n.slice(0, 40)} className="mt-4 flex gap-2.5 rounded-xl bg-[#F7FAF9] border border-[#DCE7E3] p-4 text-sm text-[#475569] leading-relaxed" data-testid="sd-note">
                  <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#0F766E]" />
                  <span>{n}</span>
                </div>
              ))}

              {result.previousHomeUnsure && (
                <div className="mt-4 flex gap-2.5 rounded-xl bg-[#FFF7E6] border border-[#FCD98B] p-4 text-sm text-[#475569] leading-relaxed" data-testid="sd-previous-home-unsure-note">
                  <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#D97706]" />
                  <span>
                    This estimate assumes your previous main home will have been sold by completion. If it hasn't,
                    the higher rates for additional properties will initially apply — please confirm your position with your conveyancer.
                  </span>
                </div>
              )}

              {result.residencyUnsure && (
                <div className="mt-4 flex gap-2.5 rounded-xl bg-[#FFF7E6] border border-[#FCD98B] p-4 text-sm text-[#475569] leading-relaxed" data-testid="sd-residency-unsure-note">
                  <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#D97706]" />
                  <span>
                    This is the standard estimate. Additional non-resident SDLT rules may apply to your purchase —
                    please confirm your residence position with your conveyancer.
                  </span>
                </div>
              )}

              <p className="mt-5 text-xs text-[#94A3B8]" data-testid="sd-rates-reviewed">
                Rates last reviewed: {RATES_LAST_REVIEWED} · Source: {result.source.name} · Rates in force from {result.effectiveFrom}.
              </p>
              <p className="mt-3 text-xs text-[#475569] leading-relaxed" data-testid="sd-disclaimer">
                This calculator provides an estimate based on the information entered and the published rates in force
                on the date shown. Property tax treatment can vary in more complex circumstances. Your solicitor or
                conveyancer will confirm the amount payable before completion.
              </p>
              <p className="mt-2 text-xs text-[#94A3B8] leading-relaxed">
                This calculator is designed for straightforward residential purchases. More complex transactions —
                such as shared ownership, purchases by companies or trusts, or mixed-use property — may be treated differently.
              </p>

              <ShareActions shareText={shareText} />
            </div>
          )}
        </div>
      </div>

      {/* Contextual Snapshot nudge — shown only once the user has a result so it feels like a natural next step, not a pushy sell */}
      {result && (
        <section className="mt-10 max-w-4xl" data-testid="sd-snapshot-nudge">
          <Link
            to="/snapshot?source=stamp_duty_snapshot"
            onClick={() => track("stamp_duty_snapshot_click", { jurisdiction, buyer_type: buyerType })}
            data-testid="sd-snapshot-nudge-link"
            className="block rounded-2xl border border-[#DCE7E3] bg-gradient-to-br from-[#0F766E]/5 via-white to-[#10B981]/5 hover:border-[#0F766E]/50 hover:from-[#0F766E]/8 hover:via-white hover:to-[#10B981]/8 transition-all p-5 sm:p-6 group"
          >
            <div className="flex items-center gap-4 sm:gap-5">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#10B981]/12 border border-[#10B981]/25 flex items-center justify-center shrink-0" aria-hidden="true">
                <Sparkles className="w-5 h-5 text-[#0B5F58]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-[#0F766E]">Now the tax — see the full picture</div>
                <h3 className="font-sora text-lg sm:text-xl font-semibold text-[#0B1220] mt-0.5 group-hover:text-[#0F766E] transition-colors">
                  Get your free Mortgage Snapshot
                </h3>
                <p className="mt-1 text-sm text-[#475569] leading-relaxed">
                  Two minutes for your estimated loan, monthly repayment and loan-to-value — with plain-English commentary. No credit check, no obligation.
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#0F766E] shrink-0 hidden sm:block group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
            </div>
          </Link>
        </section>
      )}

      {/* CTAs */}
      <section className="mt-10 max-w-4xl" onClickCapture={() => track("next_step_clicked", { cta_location: "post_stamp_duty_next_step" })}>
        <h2 className="font-sora text-2xl sm:text-3xl font-semibold text-[#0B1220] tracking-tight">Planning your purchase?</h2>
        <div onClickCapture={() => track("stamp_duty_conveyancing_click", { jurisdiction })} data-testid="sd-cta-conveyancing">
          <ConveyancingCTA source="stamp_duty_conveyancing" variant="clickable-card" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4 items-start">
          <div onClickCapture={() => track("stamp_duty_survey_click", { jurisdiction })} data-testid="sd-cta-survey" className="h-full [&>a]:mt-0 [&>a]:h-full">
            <SurveyQuoteCTA source="stamp_duty_survey" variant="clickable-card" />
          </div>
          <div data-testid="sd-cta-broker">
            <BrokerCTA source="stamp_duty_broker" variant="clickable-card" />
          </div>
        </div>
      </section>

      {cashbackPromotionEnabled && (jurisdiction === "england" || jurisdiction === "wales") && (
        <section className="mt-4 max-w-4xl">
          <CashbackPromptCard source="stamp_duty" />
        </section>
      )}

      <SeoProse sections={meta.sections} className="mt-14" />
      <div className="mt-10 max-w-4xl"><ComplianceStrip /></div>
    </div>
  );
};

export default StampDuty;
